/**
 * Manual data-ingestion control for the KPI Scorecard header.
 *
 * Everyone sees the "synced Xm ago" freshness label; only admins
 * (settings.admin_emails, mirrored in auth/admin.ts) see the Refresh button,
 * which triggers POST /api/pm/scorecard/sync/ (the same Notion poll as the
 * 10-min scheduler) and then refetches the scorecard so numbers update without
 * waiting for the next tick.
 *
 * A 60s cooldown after a successful sync keeps an admin from hammering Notion's
 * rate limit (the button is also disabled while a sync is in flight).
 */
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { isAdminEmail } from '@/auth/admin';
import { useAuth } from '@/contexts/AuthContext';
import { scorecard as scorecardApi } from '@/helpers/ApiHelper';
import { formatRelative } from '@/utils/formatRelative';

const COOLDOWN_MS = 60_000;

// Success uses the shared "sherlock:toast" CustomEvent (same convention as
// InvestigationFeedback / DeleteInvestigationDialog); errors get a red inline
// Snackbar so a failure is unmistakable.
function fireToast(message: string): void {
  window.dispatchEvent(new CustomEvent('sherlock:toast', { detail: { message } }));
}

interface SyncControlProps {
  lastSyncedAt: string | null;
  /** Called after a successful sync so the parent can refetch the scorecard. */
  onSynced: () => void;
}

export function SyncControl({ lastSyncedAt, onSynced }: SyncControlProps): ReactElement {
  const { user } = useAuth();
  const canSync = isAdminEmail(user?.email);

  const [syncing, setSyncing] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    },
    [],
  );

  async function handleSync(): Promise<void> {
    if (syncing || cooldown) return;
    setSyncing(true);
    setErrorMsg(null);
    try {
      const result = await scorecardApi.triggerSync();
      fireToast(
        `Synced ${result.tasks_synced} tasks · ${result.transitions_recorded} transitions`,
      );
      onSynced();
      setCooldown(true);
      cooldownTimer.current = setTimeout(() => setCooldown(false), COOLDOWN_MS);
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : 'Sync failed — please try again');
    } finally {
      setSyncing(false);
    }
  }

  const freshness = lastSyncedAt ? `synced ${formatRelative(lastSyncedAt)}` : 'never synced';
  const disabled = syncing || cooldown;

  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography
        aria-live="polite"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.6875rem',
          letterSpacing: '0.3px',
          color: 'text.faintest',
          whiteSpace: 'nowrap',
        }}
      >
        {freshness}
      </Typography>

      {canSync && (
        <Box
          component="button"
          type="button"
          onClick={handleSync}
          disabled={disabled}
          aria-label="Refresh scorecard data from Notion"
          aria-busy={syncing}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            px: 1.625,
            minHeight: 44,
            borderRadius: '5px',
            background: 'background.deeper',
            color: 'text.secondary',
            border: '1px solid',
            borderColor: 'divider',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.55 : 1,
            transition: 'all 140ms',
            '&:hover': disabled ? {} : { borderColor: '#353540', color: 'text.primary' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          {syncing ? (
            <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
          ) : (
            <RefreshIcon sx={{ fontSize: 15 }} />
          )}
          {syncing ? 'Syncing…' : 'Refresh'}
        </Box>
      )}

      <Snackbar
        open={errorMsg !== null}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {errorMsg ? (
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setErrorMsg(null)}
            sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' }}
          >
            Sync failed: {errorMsg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}
